// review-refs — extract clickable web-source references from the
// web_search tool output, so the chat UI can render a chip row under
// the assistant turn (mirroring project-mentions.ts + ProjectChipRow).
//
// Kept narrow on purpose: only the web_search tool produces these.
// Other tools that happen to carry URLs (e.g. data_source pointers) are
// citations of structured datasets, not "external commentary", and
// belong in a different surface (CitationChip).

const MAX_REFS = 8;

export interface ReviewRef {
  url: string;
  title: string;
  source: string;   // host or "reddit.com/r/<subreddit>"
  backend?: string; // "brave" | "reddit" — drives accent colour
  favicon?: string; // CDN'd favicon URL from the search backend, when present
}

function asObject(v: unknown): Record<string, unknown> | null {
  return v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
}

function asArray(v: unknown): unknown[] {
  return Array.isArray(v) ? v : [];
}

function asString(v: unknown): string {
  return typeof v === "string" ? v : "";
}

// normaliseURL mirrors the backend's dedupe-key shape: lowercase scheme +
// host, strip fragments and pure tracking params, trim trailing slash.
// Two URLs that point at the same canonical resource collapse to one key.
function normaliseURL(raw: string): string {
  if (!raw) return "";
  let u = raw;
  const hash = u.indexOf("#");
  if (hash >= 0) u = u.slice(0, hash);
  const qIdx = u.indexOf("?");
  if (qIdx >= 0) {
    const base = u.slice(0, qIdx);
    const parts = u.slice(qIdx + 1).split("&").filter((p) => {
      const lp = p.toLowerCase();
      return !lp.startsWith("utm_") && !lp.startsWith("fbclid=") && !lp.startsWith("gclid=");
    });
    u = parts.length > 0 ? `${base}?${parts.join("&")}` : base;
  }
  const sch = u.indexOf("://");
  if (sch >= 0) {
    const hostStart = sch + 3;
    const pathRel = u.slice(hostStart);
    const m = pathRel.match(/[\/?#]/);
    const pathStart = m ? hostStart + m.index! : u.length;
    u = u.slice(0, pathStart).toLowerCase() + u.slice(pathStart);
  }
  return u.replace(/\/+$/, "");
}

export function extractReviewRefs(toolName: string, rawOutput: string): ReviewRef[] {
  if (toolName !== "web_search" || !rawOutput) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(rawOutput);
  } catch {
    return [];
  }
  let obj = asObject(parsed);
  if (!obj) return [];

  // Tools that return *ToolEnvelope (web_search does, on the happy path)
  // wrap their payload under .model_observation; soft-failure / no_results
  // shapes are bare maps at the top level. Try the envelope shape first
  // and fall through to top-level when absent — handles both without
  // forking the extractor.
  if ("model_observation" in obj) {
    const inner = asObject(obj.model_observation);
    if (inner) obj = inner;
  }

  // Both the soft-failure shape and the no_results shape mean "nothing
  // to chip" — skip them so we don't render an orphan empty row.
  if ("error_code" in obj || obj.no_results === true) return [];

  // The tool's model_observation puts the array under `results`. Each
  // row has {url, title, snippet, source, published_at, ...}. Snippet
  // is intentionally omitted — chips are a navigation aid, not a
  // preview surface; full snippet lives in the gen_ui card.
  const out: ReviewRef[] = [];
  const seen = new Set<string>();
  for (const r of asArray(obj.results)) {
    if (out.length >= MAX_REFS) break;
    const ro = asObject(r);
    if (!ro) continue;
    const url = asString(ro.url).trim();
    if (!url) continue;
    const key = normaliseURL(url);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push({
      url,
      title: asString(ro.title).trim() || url,
      source: asString(ro.source).trim(),
      backend: asString(ro.backend) || undefined,
      favicon: asString(ro.favicon).trim() || undefined,
    });
  }
  return out;
}

// dedupeReviewRefs merges several ref lists by normalised URL,
// preserving first-seen metadata.
export function dedupeReviewRefs(
  ...lists: ReadonlyArray<readonly ReviewRef[]>
): ReviewRef[] {
  const out: ReviewRef[] = [];
  const seen = new Set<string>();
  for (const list of lists) {
    for (const ref of list) {
      const key = normaliseURL(ref.url);
      if (!key || seen.has(key)) continue;
      seen.add(key);
      out.push(ref);
    }
  }
  return out;
}
